import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useRegisterMoodAction } from '@/api/queries/dds-emotions-queries'
import { ResponsiveFormModal } from '@/components/commons'
import { Textarea } from '@/components/ui/textarea'
import type { DDSMood } from '@/types'
import { getApiErrorMessage } from '@/utils/api-error'
import { DdsMoodIcon } from './dds-mood'

const MAX_ACTION_LENGTH = 500
const FORM_ID = 'dds-mood-action-form'

export interface DdsMoodActionTarget {
	ddsId: string
	attendeeId: string
	personName: string
	mood: DDSMood
	moodAction: string | null
}

interface DdsMoodActionDialogProps {
	target: DdsMoodActionTarget | null
	onClose: () => void
}

function MoodActionForm({
	target,
	onClose,
}: {
	target: DdsMoodActionTarget
	onClose: () => void
}) {
	const { t } = useTranslation()
	const [action, setAction] = useState(target.moodAction ?? '')
	const mutation = useRegisterMoodAction()

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault()
		if (mutation.isPending) return

		try {
			await mutation.mutateAsync({
				ddsId: target.ddsId,
				attendeeId: target.attendeeId,
				action,
			})
			toast.success(
				t(
					action.trim()
						? 'ddsEmotions.action.saved'
						: 'ddsEmotions.action.cleared',
				),
			)
			onClose()
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	return (
		<form className="flex flex-col gap-3" id={FORM_ID} onSubmit={handleSubmit}>
			<p className="flex items-center gap-2 text-kaefer-dark-grey text-sm">
				<DdsMoodIcon className="size-6" mood={target.mood} />
				<span>
					{t('ddsEmotions.action.description', { name: target.personName })}
				</span>
			</p>
			<Textarea
				maxLength={MAX_ACTION_LENGTH}
				onChange={(event) => setAction(event.target.value)}
				placeholder={t('ddsEmotions.action.placeholder')}
				rows={5}
				value={action}
			/>
			<div className="flex items-center justify-between text-muted-foreground text-xs">
				<span>{t('ddsEmotions.action.clearHint')}</span>
				<span className="tabular-nums">
					{action.length}/{MAX_ACTION_LENGTH}
				</span>
			</div>
		</form>
	)
}

export function DdsMoodActionDialog({
	target,
	onClose,
}: DdsMoodActionDialogProps) {
	const { t } = useTranslation()

	return (
		<ResponsiveFormModal
			cancelLabel={t('ddsEmotions.action.cancel')}
			confirmLabel={t('ddsEmotions.action.save')}
			formId={FORM_ID}
			onCancel={onClose}
			onOpenChange={(open) => !open && onClose()}
			open={!!target}
			title={t('ddsEmotions.action.title')}
		>
			{target && (
				<MoodActionForm
					key={target.attendeeId}
					onClose={onClose}
					target={target}
				/>
			)}
		</ResponsiveFormModal>
	)
}
